/**
 * Browser GA4 context used by the signup API. Returns nulls when gtag is not
 * available (blocked, not configured, or consent denied) so signup never
 * depends on analytics availability.
 */

export const GA_MEASUREMENT_ID = 'G-VW2S0SK06N';

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

const GA_GET_TIMEOUT_MS = 1_000;

type GaValue = string | number | null;

const getGaValue = (field: 'client_id' | 'session_id'): Promise<GaValue> =>
  new Promise((resolve) => {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
      resolve(null);
      return;
    }

    let settled = false;
    const timer = window.setTimeout(() => finish(null), GA_GET_TIMEOUT_MS);
    const finish = (value: GaValue) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      resolve(value ?? null);
    };

    try {
      window.gtag('get', GA_MEASUREMENT_ID, field, (value: unknown) => {
        if (typeof value === 'string' || typeof value === 'number') {
          finish(value);
          return;
        }
        finish(null);
      });
    } catch {
      finish(null);
    }
  });

export interface GaContext {
  clientId: string | null;
  sessionId: number | null;
}

export interface GaAnalyticsPayload {
  clientId: string;
  sessionId?: number;
  eventId?: string;
}

export const getGaContext = async (): Promise<GaContext> => {
  const [clientId, sessionId] = await Promise.all([
    getGaValue('client_id'),
    getGaValue('session_id'),
  ]);

  return {
    clientId: typeof clientId === 'string' && clientId.trim() ? clientId : null,
    sessionId: (() => {
      const numeric = Number(sessionId);
      return Number.isFinite(numeric) && numeric > 0
        ? Math.trunc(numeric)
        : null;
    })(),
  };
};

const newAnalyticsEventId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

/**
 * Optional analytics payload for API requests. `eventId` is an internal
 * idempotency key used for async/retryable product events.
 */
export const getGaAnalyticsPayload = async (
  withEventId = false
): Promise<GaAnalyticsPayload | undefined> => {
  const context = await getGaContext();
  if (!context.clientId) return undefined;
  return {
    clientId: context.clientId,
    ...(context.sessionId ? { sessionId: context.sessionId } : {}),
    ...(withEventId ? { eventId: newAnalyticsEventId() } : {}),
  };
};
